import React from 'react'
import { toast, ToastOptions } from 'react-toastify'
import DOMPurify from 'dompurify'

// Check if the message contains HTML tags
const containsHtml = (message: string): boolean => /<[^>]+>/.test(message)

// Component to safely render sanitized HTML
const SanitizedHtml: React.FC<{ html: string }> = ({ html }) =>
  React.createElement('div', {
    dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(html) }, // DOMPurify.sanitize removes malicious scripts/tags from HTML while preserving safe markup. Clean HTML before rendering.
  })

// Helper to reliably extract backend error messages from Axios or RTK query errors
export const getApiErrorMsg = (err: any, fallback: string = 'An error occurred'): string => {
  if (typeof err === 'string') return err

  const msg = err?.response?.data?.message || err?.data?.message || err?.message
  let detail = 
    err?.response?.data?.data?.detail || 
    err?.response?.data?.detail ||
    err?.data?.data?.detail || 
    err?.data?.detail || 
    err?.data?.error || 
    err?.response?.data?.error

  if (Array.isArray(detail)) {
    detail = detail.join(' ')
  } else if (typeof detail === 'object' && detail !== null) {
    try {
      detail = Object.values(detail).flat().join(' ')
    } catch {
      detail = JSON.stringify(detail)
    }
  }

  if (msg && detail && msg !== detail) {
    const sep = msg.trim().endsWith('.') ? ' ' : ' - '
    return `${msg}${sep}${detail}`
  }

  return msg || detail || fallback
}

// Hook to show success, warning, and error toasts
const useAppToast = () => {
  // Return plain text or a component with safe HTML
  const renderMessage = (message: string): string | React.FC =>
    containsHtml(message)
      ? () => React.createElement(SanitizedHtml, { html: message })
      : message

  // Show a success toast
  const successToast = (message: string, options?: ToastOptions) =>
    toast.success(renderMessage(message), { type: 'success', ...options })

  // Show a warning toast
  const warningToast = (message: string, options?: ToastOptions) =>
    toast.warning(renderMessage(message), { type: 'warning', ...options })

  // Show an error toast
  const errorToast = (message: string, options?: ToastOptions) =>
    toast.error(renderMessage(message), { type: 'error', ...options })

  return { successToast, warningToast, errorToast }
}

export default useAppToast
